import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { Box, Typography, Paper } from "@mui/material";
import CustomTable, { TableHeader, TableRowData } from "../common-features/CustomTable";

interface DisbursementRequestProps {
  loading?: boolean;
}

const DisbursementRequest: React.FC<DisbursementRequestProps> = ({ loading = false }) => {
  const { expenseDetails, feesDetails, vasDetails } = useSelector((state: RootState) => state.disbursementRequestData);

  const processTableData = (rawData: any[], type: "expenses" | "fees" | "vas") => {
    let headers: TableHeader[] = [];
    let rows: TableRowData[] = [];

    if (Array.isArray(rawData) && rawData.length > 0) {
      const firstRow = rawData[0];

      if (Array.isArray(firstRow)) {
        // 1. Process VAS Table
        if (type === "vas") {
          const allowedVasKeys = [
          "productName",
          "amountToDisburse",
          "beneficiaryName",
          "bankName",
          "accountNumber",
          "ifscCode",
          "paymentMode"
          ];

          headers = allowedVasKeys.reduce((acc: TableHeader[], key) => {
            const apiItem = firstRow.find((item: any) => item.key === key);
            if (apiItem) {
              acc.push({
                key: apiItem.key,
                label: apiItem.label,
                width: key === "productName" || key === "beneficiaryName" ? "20%" : "12%" 
              });
            }
            return acc;
          }, []);
        } else {
          // 2. Process Expenses and Fees
          headers = firstRow
          .filter((item: any) => item.key !== "formType")
          .map((item: any) => {
            let label = item.label;
            let key = item.key;
            let width = "15%";

            if (type === "expenses" && key === "benificaryAccountNumber") {
              label = "Beneficiary Account Number";
              width = "18%";
            }
            if (type === "fees" && key === "feesType") {
              label = "Fees Type";
              width = "40%";
            }
      
            // Handle long names in Expenses
            if (key === "benificaryName" || key === "category") {
              width = "20%";
            }

            return { key, label, width };
          });
        }

        // Add S No. header
        headers.unshift({ key: "sno", label: "S No.", width: "60px" });

        // 3. Map Rows (No changes needed here)
        rows = rawData.map((row: any[], idx: number) => {
          const rowObj: TableRowData = { sno: idx + 1 };
          row.forEach((item: any) => {
            if (headers.find((h) => h.key === item.key)) {
              rowObj[item.key] = item.value;
            }
          });
          return rowObj;
        });
      }
    }
    return { headers, rows };
  };

  const expensesData = processTableData(expenseDetails, "expenses");
  const feesData = processTableData(feesDetails, "fees");
  const vasData = processTableData(vasDetails, "vas");

  // Helper for loading skeletons
  const TableSkeleton = (headerCount: number) => (
    <Box sx={{ width: "100%" }}>
      {[...Array(3)].map((_, i) => (
        <Box key={i} sx={{ display: "flex", gap: 2, mb: 1 }}>
          {[...Array(headerCount || 4)].map((_, j) => (
            <Box key={j} sx={{ flex: 1 }}>
              <Box sx={{ bgcolor: "#e0e0e0", height: 28, borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box>
      {/* Expenses Table */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>Expenses</Typography>
        {loading ? (
          TableSkeleton(expensesData.headers.length)
        ) : (
          <CustomTable
            headers={expensesData.headers.map((h) => ({ ...h, align: "left" }))}
            rows={expensesData.rows}
          />
        )}
      </Paper>

      {/* Fees Table */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>Fees</Typography>
        {loading ? (
          TableSkeleton(feesData.headers.length)
        ) : (
          <CustomTable
            headers={feesData.headers.map((h) => ({ ...h, align: "left" }))}
            rows={feesData.rows}
          />
        )}
      </Paper>

      {/* VAS Table */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>VAS</Typography>
        {loading ? (
          TableSkeleton(vasData.headers.length)
        ) : (
          <CustomTable
            headers={vasData.headers.map((h) => ({ ...h, align: "left" }))}
            rows={vasData.rows}
          />
        )}
      </Paper>

      {/* Consent Actions */}
      {/* <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
      <Typography
      variant="body1"
      color="text.secondary"
      mb={3}
      sx={{ fontSize: "0.95rem" }}
      >
      I confirm that all the above details are accurate and true to the best
      of my knowledge.
      </Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
      <Button
      variant="contained"
      onClick={handleAgree}
      sx={{
      textTransform: "none",
      bgcolor: "#00b3b5",
      color: "#fff",
      fontWeight: 600,
      borderRadius: 2,
      px: 3,
      "&:hover": { bgcolor: "#00999a" },
      }}
      >
      I Agree
      </Button>
      <Button
      variant="outlined"
      onClick={handleDisagree}
      sx={{
      textTransform: "none",
      borderColor: "#00b3b5",
      color: "#00b3b5",
      fontWeight: 600,
      borderRadius: 2,
      px: 3,
      "&:hover": { bgcolor: "#e0f7fa", borderColor: "#00999a" },
      }}
      >
      I Disagree
      </Button>
      </Box>
      </Paper> */}
    </Box>
  );
};

export default DisbursementRequest;
